import React from 'react';

/**
 * Tracks whether the University API lets the signed-in user in at all, for
 * the app-level "No access to Alliance University" screen.
 *
 * University has no bootstrap call that answers "may this user use the
 * app", so the answer is read from the API calls themselves: every response
 * from the University API is reported here, and the user is refused when at
 * least one call has come back 403 and none has succeeded. One success (any
 * page that loaded) means they have access, and the flag stays off for the
 * rest of the session.
 */

let forbidden = 0;
let ok = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());

const isRefused = () => forbidden > 0 && ok === 0;

/**
 * Report one University API response.
 *
 * @param status - The HTTP status; only 2xx and 403 say anything about access
 */
export const reportAppAccess = (params: { status: number | undefined }) => {
  const before = isRefused();
  if (params.status === 403) {
    forbidden += 1;
  } else if (params.status && params.status >= 200 && params.status < 300) {
    ok += 1;
  } else {
    return;
  }
  if (isRefused() !== before) {
    emit();
  }
};

/** Forget what the API said, e.g. after sign out */
export const resetAppAccess = () => {
  const before = isRefused();
  forbidden = 0;
  ok = 0;
  if (before) {
    emit();
  }
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** True while every University API call so far has been refused (403) */
export function useAppAccessRefused() {
  return React.useSyncExternalStore(subscribe, isRefused);
}
